import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import BaseModal from "../../components/BaseModal";
import BaseInput from "../../components/BaseInput";
import BaseButton from "../../components/BaseButton";
import BaseTextButton from "../../components/BaseTextButton";
import BaseError from "../../components/BaseError";
import useModalActions from "../../hooks/useModalActions";
import { MODAL_TYPES } from "../../utils/emitter";
import "./style.scss";

const CODE_LENGTH = 6;
const RESEND_AFTER_SEC = 60;

// 23.10: подтверждение почты при регистрации. Открывается из useEmailCodeConfirm,
// когда код уже отправлен; аккаунт создаётся только после верного кода.
// payload: { email, onConfirm(code) → Promise<текст ошибки | null>, onResend() → то же }
export default function EmailCodeModal() {
  const [payload, setPayload] = useState(null);
  const { t } = useTranslation();

  return (
    <BaseModal
      className="email-code-modal"
      eventTypeModal={MODAL_TYPES.EMAIL_CODE}
      onPayload={(data) => data && setPayload(data)}
      title={t("email_code.title")}
      titleStyle="email-code-modal__title"
    >
      {/* BaseModal снимает содержимое при закрытии: при новом открытии поле и
          таймер начинаются заново */}
      {payload?.email ? <EmailCodeContent payload={payload} /> : null}
    </BaseModal>
  );
}

function EmailCodeContent({ payload }) {
  EmailCodeContent.propTypes = {
    payload: PropTypes.object,
  };
  const { email, onConfirm, onResend } = payload;
  const { t } = useTranslation();
  const { closeModal } = useModalActions();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [resendIn, setResendIn] = useState(RESEND_AFTER_SEC);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  const handleChange = (e) => {
    setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH));
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isSending) return;
    if (code.length !== CODE_LENGTH) {
      setError(t("email_code.enter_code"));
      return;
    }
    setIsSending(true);
    setInfo("");
    const err = await onConfirm(code);
    // при успехе окно уже закрыто и страница ушла дальше: состояние не трогаем
    if (err) {
      setError(err);
      setIsSending(false);
    }
  };

  const resend = async () => {
    if (resendIn > 0 || isSending) return;
    setIsSending(true);
    setError("");
    const err = await onResend();
    setIsSending(false);
    if (err) {
      setError(err);
      return;
    }
    setCode("");
    setInfo(t("email_code.resent"));
    setResendIn(RESEND_AFTER_SEC);
  };

  return (
    <div className="email-code-modal__content">
      <p className="email-code-modal__text">
        {t("email_code.text", { email })}
      </p>
      <p className="email-code-modal__hint">{t("email_code.spam_hint")}</p>
      <form className="email-code-modal__form" onSubmit={submit}>
        <BaseInput
          id="email-code"
          name="emailCode"
          type="text"
          inputMode="numeric"
          autocomplete="one-time-code"
          maxlength={CODE_LENGTH}
          value={code}
          onChange={handleChange}
          label={t("email_code.code_label")}
        />
        <div className="email-code-modal__error">
          <BaseError errorMessage={error} />
          {info && !error ? (
            <p className="email-code-modal__info">{info}</p>
          ) : null}
        </div>
        <BaseButton type="submit" loading={isSending}>
          {t("email_code.confirm")}
        </BaseButton>
      </form>
      {/* вне <form>: BaseTextButton рендерит <button>, внутри формы он отправил бы её */}
      <div className="email-code-modal__links">
        {resendIn > 0 ? (
          <span className="email-code-modal__countdown">
            {t("email_code.resend_in", { sec: resendIn })}
          </span>
        ) : (
          <BaseTextButton onClick={resend}>
            {t("email_code.resend")}
          </BaseTextButton>
        )}
        <BaseTextButton onClick={() => closeModal(MODAL_TYPES.EMAIL_CODE)}>
          {t("email_code.change_email")}
        </BaseTextButton>
      </div>
    </div>
  );
}
